import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, Boxes, Timer, Recycle,
  ArrowRight, Quote, Calculator,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const FLAME = "#FF4500";

/* ---------- editable assumptions ---------- */
const LIFECYCLE_YRS = 3;
// 1 · Portfolio optimization
const UTIL_UPLIFT = 35;        // % higher utilization / faster turns
const ALT_INVENTORY = 1000;    // alt mats needed for portfolio
const FOOTPRINT_CUT = 30;      // % smaller inventory footprint
const BBM_PRICE = 1900;        // Big Bad Mats unit purchase price
// 2 · Labor & schedule compression
const DEPLOY = 5000;           // mats in typical deployment
const TRAD_HR_PER_MAT = 0.5;   // traditional install crew-hrs / mat
const INSTALL_CUT = 40;        // % faster install (fast-connect)
const CREW_RATE = 85;          // $/crew-hr (burdened)
const CREW_SIZE = 12;
const SHIFT_HR = 10;
// 3 · Replacement & disposal avoidance
const FAIL_RATE = 15;          // % annual breakage on composite mats
const REPLACE_UNIT = 1200;     // re-order cost / mat
const DISPOSAL_FEE = 150;      // landfill disposal / mat

/* ---------- derived ---------- */
const OUR_INVENTORY = Math.round(ALT_INVENTORY * (1 - FOOTPRINT_CUT / 100));
const FEWER_ASSETS = ALT_INVENTORY - OUR_INVENTORY;
const acquisitionCapital = OUR_INVENTORY * BBM_PRICE;
const oursHrPerMat = TRAD_HR_PER_MAT * (1 - INSTALL_CUT / 100);
const tradHours = DEPLOY * TRAD_HR_PER_MAT;
const oursHours = DEPLOY * oursHrPerMat;
const savedHours = tradHours - oursHours;
const laborSavings = savedHours * CREW_RATE;
const daysAccel = Math.round(savedHours / (CREW_SIZE * SHIFT_HR));
const replaced = Math.round(DEPLOY * (FAIL_RATE / 100) * LIFECYCLE_YRS);
const disposalAvoided = replaced * (REPLACE_UNIT + DISPOSAL_FEE);
const totalQuantified = laborSavings + disposalAvoided;

const usd = (n) => "$" + n.toLocaleString();
const usdM = (n) => "$" + (n / 1e6).toFixed(2) + "M";
const num = (n) => n.toLocaleString();

const PILLARS = [
  {
    icon: Boxes,
    tag: "01",
    title: "Acquisition & Portfolio Optimization",
    kicker: "Buy fewer assets",
    math: `Alt inventory (${num(ALT_INVENTORY)}) − Big Bad (${num(OUR_INVENTORY)}) × ${usd(BBM_PRICE)}/mat`,
    result: `${FEWER_ASSETS} fewer mats`,
    resultSub: `−${FOOTPRINT_CUT}% footprint · ${usd(BBM_PRICE)}/mat · ${usdM(acquisitionCapital)} deployed`,
    bars: [
      { label: "Alternative mats", val: ALT_INVENTORY, color: "#8A93A0" },
      { label: "Big Bad Mats", val: OUR_INVENTORY, color: FLAME },
    ],
    barMax: ALT_INVENTORY,
    barFmt: num,
    talk: `Because our mats feature ${UTIL_UPLIFT}% higher utilization and faster turn times, your total required inventory footprint across the ${LIFECYCLE_YRS}-year project portfolio shrinks by ${FOOTPRINT_CUT}%. You are buying fewer physical assets to achieve the same operational footprint.`,
  },
  {
    icon: Timer,
    tag: "02",
    title: "Labor & Schedule Compression",
    kicker: "Installation ROI",
    math: `(Trad ${num(tradHours)} hrs × ${usd(CREW_RATE)}) − (Ours ${num(oursHours)} hrs × ${usd(CREW_RATE)})`,
    result: usd(laborSavings),
    resultSub: `${num(savedHours)} crew hrs · +${daysAccel} days faster`,
    bars: [
      { label: "Traditional install", val: tradHours, color: "#8A93A0" },
      { label: "Fast-connect install", val: oursHours, color: FLAME },
    ],
    barMax: tradHours,
    barFmt: (v) => `${num(v)} hrs`,
    talk: `Our fast-connect system cuts installation time by ${INSTALL_CUT}% per mile of haul road. On a typical ${num(DEPLOY)}-mat deployment, this saves ${num(savedHours)} crew hours — translating directly to ${usd(laborSavings)} in direct labor savings and accelerating your mobilization milestone by ${daysAccel} days.`,
  },
  {
    icon: Recycle,
    tag: "03",
    title: "Replacement & Disposal Avoidance",
    kicker: "Longevity ROI",
    math: `${num(replaced)} replacements × (${usd(REPLACE_UNIT)} + ${usd(DISPOSAL_FEE)} disposal) vs $0`,
    result: usdM(disposalAvoided),
    resultSub: `0% replacement rate · ${LIFECYCLE_YRS}-yr lifecycle`,
    bars: [
      { label: `Composite (${FAIL_RATE}%/yr fail)`, val: replaced, color: "#8A93A0" },
      { label: "Big Bad Mats", val: 0, color: FLAME },
    ],
    barMax: replaced,
    barFmt: (v) => `${num(v)} replaced`,
    talk: `Competing composite mats suffer an average ${FAIL_RATE}% failure/breakage rate per year on heavy industrial sites, requiring re-ordering and landfill disposal fees. Our mats hold a certified lifespan that yields a 0% replacement rate over the project lifecycle — wiping out ${usdM(disposalAvoided)} in secondary procurement costs.`,
  },
];

export default function TCOFramework() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">TCO Framework · One-Page Brief</span>
            <a href="/big-bad-mats-tco-framework.pdf" download data-testid="download-tco-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-tco-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 LANDSCAPE SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="tco-brief-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[64%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Financial ROI Framework · For the Director of Procurement</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[32px]">
                From “Cost per Mat” to Total Cost of Ownership.
              </h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">
                The winning procurement case for <span className="font-semibold text-ink">Big Bad Mats</span> is built on <span className="font-semibold text-ink">TCO per project lifecycle</span> — not unit price.
                Three quantifiable levers move the number that actually hits the project P&amp;L.
              </p>
            </div>
            {/* mindset shift + headline value */}
            <div className="w-[33%] flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 text-[11px] font-display font-bold">
                <span className="line-through text-muted-foreground">Cost per Mat</span>
                <ArrowRight className="h-4 w-4 text-flame" />
                <span className="text-ink">TCO per Lifecycle</span>
              </div>
              <div className="w-full bg-ink text-white p-3 text-right">
                <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">Quantified {LIFECYCLE_YRS}-yr advantage*</div>
                <div className="font-display font-extrabold text-flame text-[26px] leading-none mt-1">{usdM(totalQuantified)}</div>
                <div className="text-[8px] text-white/50 mt-1">+ {FEWER_ASSETS} fewer assets purchased</div>
              </div>
            </div>
          </header>

          {/* three pillars */}
          <div className="flex-1 grid grid-cols-3 gap-4 pt-4 min-h-0">
            {PILLARS.map((p) => (
              <div key={p.tag} data-testid={`pillar-${p.tag}`} className="border border-border flex flex-col min-h-0">
                {/* pillar head */}
                <div className="bg-ink text-white px-4 py-3 flex items-center gap-3">
                  <span className="font-display font-black text-flame text-[22px] leading-none">{p.tag}</span>
                  <div>
                    <div className="text-[8px] uppercase tracking-[0.18em] text-flame">{p.kicker}</div>
                    <div className="font-display font-bold leading-tight text-[13px]">{p.title}</div>
                  </div>
                </div>

                <div className="p-4 flex-1 flex flex-col">
                  {/* the math */}
                  <div className="flex items-center gap-1.5 text-[8px] uppercase tracking-[0.18em] text-muted-foreground">
                    <Calculator className="h-3 w-3 text-flame" /> The Math
                  </div>
                  <div className="mt-1 bg-muted border border-border px-3 py-2 font-mono text-[10px] text-charcoal leading-snug">
                    {p.math}
                  </div>

                  {/* result */}
                  <div className="mt-3 flex items-end justify-between border-b border-border pb-3">
                    <div>
                      <div className="font-display font-extrabold text-flame text-[26px] leading-none">{p.result}</div>
                      <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">{p.resultSub}</div>
                    </div>
                    <p.icon className="h-8 w-8 text-ink/15" />
                  </div>

                  {/* comparison bars */}
                  <div className="mt-3 space-y-2">
                    {p.bars.map((b) => (
                      <div key={b.label}>
                        <div className="flex justify-between text-[9px] mb-0.5">
                          <span className="text-charcoal">{b.label}</span>
                          <span className="font-mono font-semibold text-ink">{p.barFmt(b.val)}</span>
                        </div>
                        <div className="h-2.5 bg-muted overflow-hidden">
                          <div className="h-full" style={{ width: `${p.barMax ? Math.max((b.val / p.barMax) * 100, b.val === 0 ? 0 : 3) : 0}%`, backgroundColor: b.color }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* talking point */}
                  <div className="mt-auto pt-3">
                    <div className="flex items-center gap-1.5 text-[8px] uppercase tracking-[0.18em] text-flame mb-1">
                      <Quote className="h-3 w-3" /> The Talking Point
                    </div>
                    <p className="text-charcoal text-[10px] leading-snug italic">“{p.talk}”</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* footer */}
          <div className="mt-3 pt-2 border-t border-border flex items-center justify-between gap-4">
            <p className="text-[8px] text-muted-foreground leading-tight max-w-[64%]">
              *Illustrative model over a {LIFECYCLE_YRS}-year project portfolio — {num(DEPLOY)}-mat deployment, {usd(CREW_RATE)}/crew-hr, {INSTALL_CUT}% faster install, {FAIL_RATE}%/yr composite failure at {usd(REPLACE_UNIT)} re-order + {usd(DISPOSAL_FEE)} disposal. Actual figures vary by site conditions, utilization &amp; scope. Not a quote, guarantee, or warranty of any specific result; provided for evaluation only and not financial, engineering, or legal advice. © {new Date().getFullYear()} {COMPANY.legal}.
            </p>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-display font-bold text-ink text-[12px] tracking-tight">Build your project's TCO business case.</div>
                <div className="text-[9px] text-muted-foreground">{COMPANY.phone} · {COMPANY.email}</div>
              </div>
              <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors print:hidden">
                Request TCO Model
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
